import dayjs from "dayjs";
import { getPricingRules } from "../../../../utils/pricingRulesCache";

// TIC-10890: helpers that keep a manual discount's approver on the cart line (item.approval) and on
// its manualDiscountData entry, so every re-apply (customer add/remove, coupon, add item, remove-all,
// Custom Mix/grouped products) writes the same approver back instead of losing it.

// Config-only fields of an mPricingApprovals row — never stored on the cart or sent to the server.
const CONFIG_ONLY_FIELDS = ["pin", "type", "min", "max", "roleId", "mPricingrulesId"];

// matchingApproval passed into CheckoutPercentage/Flat/TotalManualDiscount:
//   object    → approver validated in the approval drawer (fresh apply), or the snapshot stored on the
//               manualDiscountData entry (re-apply) — returned without config fields, time kept/assigned
//   null      → fresh apply that needed no approval — this rule's approval is removed
//   undefined → nothing to change (carts saved before this fix, coupons, anything without a userId)
export const normaliseApprover = (matchingApproval) => {
  if (matchingApproval === null) return null;
  if (!matchingApproval?.userId) return undefined;
  const approver = { ...matchingApproval };
  CONFIG_ONLY_FIELDS.forEach((key) => delete approver[key]);
  approver.time = matchingApproval.time ?? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  return approver;
};

// Approver to keep on a manualDiscountData entry — a re-apply without one keeps the entry's previous approver.
export const storedApprover = (approver, previousEntry) => (approver === undefined ? (previousEntry?.approver ?? null) : approver);

// Writes, replaces (in place) or removes this rule's record in a line's approval array.
export const applyApprovalRecord = (approvalData, ruleId, approver) => {
  const list = Array.isArray(approvalData) ? approvalData.filter(Boolean) : [];
  if (approver === undefined) return list;
  const index = list.findIndex((a) => a.mPricingrulesId === ruleId);
  if (approver === null) return index === -1 ? list : list.filter((_, i) => i !== index);
  const record = { ...approver, mPricingrulesId: ruleId };
  if (index === -1) return [...list, record];
  return list.map((a, i) => (i === index ? record : a));
};

// Removes this rule's record when its discount is reset on a line.
export const removeRuleApproval = (approvalData, ruleId) => (Array.isArray(approvalData) ? approvalData.filter((a) => a && a.mPricingrulesId !== ruleId) : []);

// The approved rule's own discountType ("P" = percentage, "V" = amount). Falls back to the line's
// allDiscounts entry when the rule is no longer in the pricing-rules cache: its discountType is "PD"
// for every percentage discount and "FD" for every amount discount (line-level and total-bill alike).
const ruleDiscountType = (ruleId, allDiscounts) => {
  const rule = getPricingRules().find((r) => r.mPricingrulesId === ruleId);
  if (rule?.discountType === "P" || rule?.discountType === "V") return rule.discountType;
  const lineType = allDiscounts.find((d) => d.mPricingruleId === ruleId)?.discountType;
  return lineType === "PD" ? "P" : lineType === "FD" ? "V" : null;
};

// "Approval" order-line meta: the approver who validated the discount, the approved rule's
// discountType (P / V), the % the line actually received (0 for amount discounts) and the line's
// discount amount. Grouped / Custom Mix child lines fall back to their parent's approval and
// allDiscounts. Returns null when there is nothing to send.
export const buildApprovalMeta = (line, precision = 2, parent = null) => {
  const approvalList = line?.approval?.length > 0 ? line.approval : parent?.approval;
  const discountAmount = parseFloat(Math.abs(parseFloat(line?.discount) || 0).toFixed(precision));
  if (!(approvalList?.length > 0) || !approvalList[0] || !(discountAmount > 0)) return null;

  const approver = { ...approvalList[0] };
  const ruleId = approver.mPricingrulesId;
  CONFIG_ONLY_FIELDS.forEach((key) => delete approver[key]);

  const allDiscounts = line?.allDiscounts?.length > 0 ? line.allDiscounts : parent?.allDiscounts || [];
  const discountType = ruleDiscountType(ruleId, allDiscounts);
  const base = (parseFloat(line.realPrice) || 0) * Math.abs(parseFloat(line.weight) || 0);

  return {
    key: "Approval",
    value: JSON.stringify([
      {
        ...approver,
        discountType,
        percentageApplied: discountType === "P" && base > 0 ? parseFloat(((discountAmount / base) * 100).toFixed(2)) : 0,
        discountAmount,
      },
    ]),
  };
};

// Line meta with the "Approval" entry replaced by this line's own (or removed when it has none).
export const withApprovalMeta = (meta, approvalMeta) => [...(meta || []).filter((m) => m.key !== "Approval"), ...(approvalMeta ? [approvalMeta] : [])];
